import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { KBOPlayerYearStatsListItemDto } from '@playball/common/dto/KBO';
import { DataListResponse } from '@playball/common/dto/Common';
import { HomeSectionProps } from './HomeSectionProps';

const WeeklyRankings: React.FC<HomeSectionProps> = ({ className }) => {
  const { fetchApi } = useAuth();
  const [allPlayers, setAllPlayers] = useState<KBOPlayerYearStatsListItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const year = 2025;
  const limit = 100;

  const fetchAllPlayers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data: DataListResponse<KBOPlayerYearStatsListItemDto> = await fetchApi(`/api/player?year=${year}&limit=${limit}`);
      
      if (Array.isArray(data.dataList)) {
        setAllPlayers(data.dataList as KBOPlayerYearStatsListItemDto[]);
      } else {
        console.error("API /api/player/2025 returned non-array data:", data);
        setError("유효하지 않은 선수 데이터를 수신했습니다.");
      }
    } catch (err: any) {
      console.error("선수 데이터를 불러오는 데 실패했습니다:", err);
      setError("랭킹을 불러오는 데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => {
    fetchAllPlayers();
  }, [fetchAllPlayers]);

  const pitcherRankings = useMemo(() => {
    return allPlayers
      .filter(player => player.position === 'P' && player.average !== null)
      .sort((a, b) => b.average - a.average)
      .slice(0, 10);
  }, [allPlayers]);

  const hitterRankings = useMemo(() => {
    return allPlayers
      .filter(player => player.position !== 'P' && player.average !== null)
      .sort((a, b) => b.average - a.average)
      .slice(0, 10);
  }, [allPlayers]);

  if (isLoading) {
    return (
      <section className={`py-5 text-center ${className ?? ''}`}>
        <div className="container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading rankings...</span>
          </div>
          <p className="mt-2 text-muted">선수 랭킹 로딩 중...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={`py-5 text-center ${className ?? ''}`}>
        <div className="container">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="weekly-rankings" className={`py-5 keep-all ${className ?? ''}`}>
      <div className="container py-5">
        <div className="text-center mb-5">
          <span className="badge rounded-pill text-bg-info bg-opacity-75 mb-3">연간 랭킹</span>
          <h2 className="fw-bold">2025년 최고의 선수들</h2>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
            2025년 연간 랭킹에서 당신의 팀 선수들을 확인하세요.
          </p>
        </div>

        <div className="row g-4 justify-content-center">
          <div className="col-lg-5 col-md-6">
            <Link to="/ranking" className="card shadow-sm h-100 text-decoration-none text-dark hover-effect">
              <div className="card-body p-4">
                <h4 className="card-title fw-bold mb-3 text-primary">투수 랭킹 TOP 10</h4>
                <ul className="list-group list-group-flush">
                  {pitcherRankings.length > 0 ? (
                    pitcherRankings.map((player, index) => (
                      <li key={player.player_id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                        <span className="fw-semibold me-2">{index + 1}.</span>
                        <span className="flex-grow-1">{player.name} ({player.team})</span>
                        <span className="text-end fw-bold">{player.average.toFixed(2)}</span>
                      </li>
                    ))
                  ) : (
                    <li className="list-group-item text-muted text-center">랭킹 정보가 없습니다.</li>
                  )}
                </ul>
                <div className="text-end mt-3">
                  <span className="btn btn-sm btn-outline-primary">더보기 <i className="bi bi-arrow-right"></i></span>
                </div>
              </div>
            </Link>
          </div>

          <div className="col-lg-5 col-md-6">
            <Link to="/ranking" className="card shadow-sm h-100 text-decoration-none text-dark hover-effect">
              <div className="card-body p-4">
                <h4 className="card-title fw-bold mb-3 text-danger">타자 랭킹 TOP 10</h4>
                <ul className="list-group list-group-flush">
                  {hitterRankings.length > 0 ? (
                    hitterRankings.map((player, index) => (
                      <li key={player.player_id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                        <span className="fw-semibold me-2">{index + 1}.</span>
                        <span className="flex-grow-1">{player.name} ({player.team})</span>
                        <span className="text-end fw-bold">{player.average.toFixed(2)}</span>
                      </li>
                    ))
                  ) : (
                    <li className="list-group-item text-muted text-center">랭킹 정보가 없습니다.</li>
                  )}
                </ul>
                <div className="text-end mt-3">
                  <span className="btn btn-sm btn-outline-danger">더보기 <i className="bi bi-arrow-right"></i></span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
      <style>{`
        .hover-effect {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hover-effect:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important;
        }
        .list-group-item {
            border-color: rgba(0, 0, 0, .05);
        }
        .list-group-item:last-child {
            border-bottom: none;
        }
      `}</style>
    </section>
  );
};

export default WeeklyRankings;